import { useIsMutating } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ExternalLink } from '@app/components';
import { passwordRegEx, paths } from '@app/constants';
import { InputPassword, Input } from '@app/pages/public/components';
import { isPhoneValid, useElementWidth } from '@app/utils';
import { Namespace } from '@data/index';
import { isValidEmailStrict } from '@data/utils/validation';
import { Button, AntdForm, PhoneInput, AntdTypography, AntdDivider } from '@ui';
import { GoogleSignUpButton } from '..';
import { useSignUpTracking } from '../../hooks';
import '../../sign-up.css';
import { useCreateUser } from './hooks';

const { Text, Title } = AntdTypography;

export const UserInfoForm = () => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const isMutating = !!useIsMutating();
  const { trackSignUpFormStarted } = useSignUpTracking();
  const { elementRef, elementWidth } = useElementWidth<HTMLElement>(200);

  const { form, handleCreateUser, initialValues, editUserMode } = useCreateUser();

  return (
    <AntdForm
      name="create-user-form"
      form={form}
      layout="vertical"
      className="sing-up-form mt-6"
      disabled={isMutating}
      autoComplete="off"
      initialValues={initialValues}
      onFinish={handleCreateUser}
      onFocus={trackSignUpFormStarted}
    >
      <Title
        ref={elementRef}
        level={2}
        className="text-black md:text-2xl lg:text-3xl mb-2 mt-0 md:mt-6"
      >
        {t('admin:sign_up.title')}
      </Title>
      <Text className="text-base-800 mb-5 inline-block">
        <Trans
          t={t}
          i18nKey="admin:sign_up.summary"
          components={{
            a: (
              <a
                href={t('admin:sign_up.url')}
                rel="noreferrer"
                target="_blank"
                className="underline text-base-800"
              />
            ),
          }}
        />
      </Text>
      <div className="w-full flex justify-center">
        <GoogleSignUpButton width={elementWidth} />
      </div>
      <AntdDivider>
        <Text className="px-4 text-gray-500 text-center w-full text-base min-w-max">
          {t('sign_up.sign_up_with_email')}
        </Text>
      </AntdDivider>
      <AntdForm.Item
        label={t('sign_up.email')}
        className="font-semibold"
        name="email"
        rules={[
          { required: true, message: t('patient:validations.required') },
          () => ({
            validator(_, value) {
              if (value && !isValidEmailStrict(value))
                return Promise.reject(new Error(t('patient:validations.email')));
              return Promise.resolve();
            },
          }),
        ]}
        validateTrigger="onBlur"
      >
        <Input data-testid="email-input" inputMode="email" size="large" />
      </AntdForm.Item>
      <AntdForm.Item
        name="phoneNumber"
        label={t('sign_up.phone_number')}
        className="mb-4 w-full font-semibold"
        rules={[
          {
            validator: (_, val: string) =>
              val && !isPhoneValid(val)
                ? Promise.reject(t('patient:validations.phone_number_is_invalid'))
                : Promise.resolve(),
          },
        ]}
      >
        <PhoneInput
          placeholder="+32 999 99 99 99"
          className="[&>input]:w-full custom-phone-input rounded-large"
          disabled={isMutating}
          isPublicPage
        />
      </AntdForm.Item>
      <AntdForm.Item
        label={t('sign_up.first_name')}
        name="firstName"
        className="font-semibold"
        rules={[{ required: true, type: 'string', message: t('patient:validations.required') }]}
      >
        <Input data-testid="first-name-input" size="large" />
      </AntdForm.Item>
      <AntdForm.Item
        label={t('sign_up.last_name')}
        name="lastName"
        className="font-semibold"
        rules={[{ required: true, type: 'string', message: t('patient:validations.required') }]}
      >
        <Input data-testid="last-name-input" size="large" />
      </AntdForm.Item>
      {!editUserMode && (
        <>
          <AntdForm.Item
            label={t('sign_up.password')}
            name="password"
            className="font-semibold"
            rules={[
              {
                message: t('patient:validations.password'),
                pattern: passwordRegEx,
                required: true,
              },
            ]}
          >
            <InputPassword data-testid="password-input" size="large" />
          </AntdForm.Item>
          <AntdForm.Item
            label={t('sign_up.confirm_password')}
            name="confirmPassword"
            className="font-semibold"
            dependencies={['password']}
            rules={[
              {
                required: true,
                message: t('patient:validations.confirm_password'),
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('patient:validations.passwords_not_match')));
                },
              }),
            ]}
          >
            <InputPassword data-testid="confirm-password-input" size="large" />
          </AntdForm.Item>
        </>
      )}
      <div className="mb-6">
        <Trans
          i18nKey="admin:b2b_terms_privacy_text"
          components={[
            <a
              key="privacy_link"
              href={t('patient:privacy_policy.b2b_link')}
              rel="noreferrer"
              target="_blank"
              className="underline text-primary-900"
            />,
            <a
              href={t('patient:terms_of_use.b2b_link')}
              rel="noreferrer"
              target="_blank"
              key="terms"
              className="underline text-primary-900"
            />,
          ]}
        />
      </div>
      <Button
        type="primary"
        className="w-full h-10"
        htmlType="submit"
        loading={isMutating}
        data-testid="submit-button"
      >
        {t('sign_up.start_for_free')}
      </Button>

      <div className="mt-4 flex flex-col gap-4">
        <Text>
          <Trans
            t={t}
            i18nKey="sign_up.already_have_account"
            components={[
              <Link
                to={`/${paths.sign_in}`}
                key="link"
                className="underline text-base-900"
                data-testid="sign-in-link"
              />,
            ]}
          />
        </Text>
        <Text>
          <Trans
            t={t}
            i18nKey="sign_up.privacy_notice"
            components={[
              <ExternalLink
                href={t('patient:privacy_policy.link')}
                key="privacy"
                className="underline text-primary-900"
              />,
            ]}
          />
        </Text>
      </div>
    </AntdForm>
  );
};
